import { visibleMenuItems, canAccess, isMenuItemVisible, GRUPOS_RANKINGS } from "@/utils/menu-map";

/**
 * Catálogo da busca global da barra superior (components/global-search.vue).
 *
 * NÃO tem regra de visibilidade própria: o menu vem de `visibleMenuItems`
 * (utils/menu-map.js, a mesma fonte do menu lateral e do tour) e as páginas da
 * conta usam o mesmo `canAccess` do menu do avatar (components/nav-bar.vue).
 * Logo a busca só encontra o que o usuário já enxerga no menu — inclusive a
 * secretária, que só acha as telas liberadas para ela.
 *
 * Cada resultado:
 *   label    texto principal
 *   context  onde fica ("APVS Brasil", "Minha conta"...)
 *   path     rota interna — OU —
 *   menuKey  item externo (CRM, BI Teams, Comunidade): o clique é repassado ao
 *            próprio item do menu lateral, que já sabe fazer o SSO.
 *   icon     classe boxicons
 *   terms    texto extra que também casa na busca (descrição do tour)
 */

const ICONES = {
  home: "bx-home-alt",
  "minhas-metas": "bx-target-lock",
  "meu-cartao": "bx-id-card",
  informativos: "bx-news",
  eventos: "bx-calendar-event",
  comunidade: "bx-group",
  crm: "bx-briefcase-alt-2",
  usuarios: "bx-user-plus",
  apn: "bx-envelope",
  biteams: "bx-bar-chart-alt-2",
  rede: "bx-network-chart",
  "apvs-brasil": "bx-line-chart",
  "apvs-truck": "bx-car",
  rankings: "bx-trophy",
  afiliados: "bx-user-voice",
  acessa: "bx-heart",
  downloads: "bx-download",
  leads: "bx-filter-alt",
  financeiro: "bx-receipt",
  suporte: "bx-support",
  guia: "bx-video",
};

function contextoRanking(child) {
  const grupo = GRUPOS_RANKINGS.find((g) => g.key === child.group);
  return grupo ? "Rankings › " + grupo.label : "Rankings";
}

function itensDoMenu(user) {
  const itens = [];
  visibleMenuItems(user).forEach((item) => {
    const icon = ICONES[item.key] || "bx-right-arrow-alt";

    if (item.children && item.children.length) {
      item.children.forEach((child) => {
        itens.push({
          id: `menu:${item.key}:${child.key}`,
          label: child.label,
          context: child.group ? contextoRanking(child) : item.label,
          path: child.path,
          icon,
          terms: item.label + " " + (item.description || ""),
        });
      });
      return;
    }

    itens.push({
      id: `menu:${item.key}`,
      label: item.label,
      context: item.external ? "Abre em outro sistema" : "Menu",
      path: item.external ? null : item.path,
      menuKey: item.external ? item.key : null,
      icon,
      terms: item.description || "",
    });
  });
  return itens;
}

// Mesmas telas (e as mesmas condições) do menu do avatar em nav-bar.vue.
const PAGINAS_CONTA = [
  { path: "/account", label: "Minha Conta", icon: "bx-user", terms: "perfil dados foto ranking privacidade" },
  { path: "/account/indication", label: "Link de Indicação", icon: "bx-link", terms: "indicar convite consultor" },
  { path: "/documents", label: "Meus Documentos", icon: "bx-file", terms: "documentos contrato" },
  { path: "/account/address", label: "Meus Endereços", icon: "bx-map", terms: "endereço cep" },
  { path: "/account/password", label: "Minha Senha", icon: "bx-lock-alt", terms: "senha trocar alterar" },
];

function itensDaConta(user) {
  const itens = PAGINAS_CONTA.filter((p) => canAccess(user, p.path)).map((p) => ({
    id: "conta:" + p.path,
    label: p.label,
    context: "Minha conta",
    path: p.path,
    icon: p.icon,
    terms: p.terms,
  }));

  if (isMenuItemVisible(user, "meu-cartao")) {
    itens.push({ id: "conta:/meu-cartao", label: "Meu Cartão", context: "Minha conta", path: "/meu-cartao", icon: "bx-id-card", terms: "cartão de visita" });
  }
  return itens;
}

const ITENS_SISTEMA = [
  { id: "sistema:novidades", label: "Novidades do sistema", context: "Sistema", path: "/novidades", icon: "bx-gift", terms: "versão atualização changelog o que mudou" },
  { id: "sistema:tutorial", label: "Como funciona (tutorial)", context: "Sistema", action: "tutorial", icon: "bx-help-circle", terms: "ajuda tour rever tutorial" },
  { id: "sistema:tema", label: "Alternar modo claro/escuro", context: "Sistema", action: "tema", icon: "bx-moon", terms: "tema dark escuro claro noturno" },
];

export function catalogoBusca(user) {
  if (!user) return [];
  return [...itensDoMenu(user), ...itensDaConta(user), ...ITENS_SISTEMA];
}

// "Adesões" casa com "adesoes", "ADESÃO" etc.
export function normalizar(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Todas as palavras digitadas precisam aparecer (em qualquer ordem). Rótulo
 * pesa mais que contexto, que pesa mais que a descrição; começo de palavra
 * pesa mais que meio.
 */
export function buscar(catalogo, consulta, limite = 12) {
  const termos = normalizar(consulta).split(/\s+/).filter(Boolean);
  if (!termos.length) return [];

  return catalogo
    .map((item) => {
      const label = normalizar(item.label);
      const context = normalizar(item.context);
      const extra = normalizar(item.terms);
      let pontos = 0;

      for (const t of termos) {
        if (label.startsWith(t)) pontos += 6;
        else if (new RegExp("\\b" + t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).test(label)) pontos += 4;
        else if (label.includes(t)) pontos += 3;
        else if (context.includes(t)) pontos += 2;
        else if (extra.includes(t)) pontos += 1;
        else return null;
      }
      return { item, pontos };
    })
    .filter(Boolean)
    .sort((a, b) => b.pontos - a.pontos || a.item.label.localeCompare(b.item.label))
    .slice(0, limite)
    .map((r) => r.item);
}

/** Sugestões com a busca vazia: os atalhos mais usados que o usuário enxerga. */
export function sugestoes(catalogo) {
  const preferidos = ["menu:home", "menu:minhas-metas", "menu:apvs-brasil:adesoes", "menu:apvs-truck:adesoes", "conta:/account", "sistema:novidades"];
  const achados = preferidos.map((id) => catalogo.find((i) => i.id === id)).filter(Boolean);
  return achados.length ? achados : catalogo.slice(0, 6);
}
